/**
 * Catchball Loop Types
 * 
 * A Catchball Loop is a convergence mechanism inside a DEGAP cycle.
 * It represents one complete pass of: intent → multi-level work → synthesis → return.
 * 
 * The ball moves: Executive → Middle → Bottom → Middle → Executive
 * This can repeat as many times as needed until priorities are stable.
 */

// ============================================================================
// Core Types
// ============================================================================

export type CatchballLoopId = string;

/**
 * The state of a Catchball Loop
 * Ball possession is derived from this state.
 * 
 * v2.2 Canonical States: OPEN, PROPOSED, REVISION_REQUESTED, ACCEPTED, FINAL_REJECTED, DECLINED
 * Legacy states are preserved for backward compatibility.
 */
export type CatchballLoopState = 
  // v2.2 Canonical States
  | 'OPEN'                 // Work in progress at any level
  | 'PROPOSED'             // Proposal submitted, awaiting response
  | 'REVISION_REQUESTED'   // Response requested additional work
  | 'ACCEPTED'             // Proposal accepted
  | 'FINAL_REJECTED'       // Proposal finally rejected (no further revision)
  | 'DECLINED'             // Loop explicitly declined/closed
  // Legacy states (mapped to canonical via normalize_loop_state in DB)
  | 'drafting'             // L0 - Loop being set up → OPEN
  | 'tossed_to_middle'     // L1 - Executive sent intent to middle layer → OPEN
  | 'middle_working'       // L2 - Middle layer running sessions/coordinating → OPEN
  | 'tossed_to_bottom'     // L3 - Middle sent work to IC layer → OPEN
  | 'bottom_working'       // L4 - ICs participating in sessions → OPEN
  | 'returned_to_middle'   // L5 - ICs returned outputs to middle → OPEN
  | 'returned_to_top'      // L6 - Middle sent synthesis to executive → PROPOSED
  | 'executive_review'     // L7 - Executive reviewing synthesis → PROPOSED
  | 'output_ready'         // L8 - Proposed set ready (A complete) → ACCEPTED
  | 'closed'               // L9 - Superseded or complete → DECLINED
  // Proposal packet states (OST-backed proposals)
  | 'proposed'             // P1 - Proposal packet submitted → PROPOSED
  | 'accepted_provisional' // P2 - Exec accepted provisionally → ACCEPTED
  | 'returned'             // P3 - Exec returned with blockers → REVISION_REQUESTED
  | 'rejected'             // P4 - Exec rejected proposal → FINAL_REJECTED
  | 'revision_requested'   // Explicit revision request state → REVISION_REQUESTED
  // What a full accept writes since migration 692 (glossary: "Loop state →
  // `accepted`; `closed_at` set"). Every accepted loop on production and
  // staging carries this lowercase value.
  | 'accepted';            // → ACCEPTED

/**
 * v2.2 Canonical Loop State (for new code)
 */
export type CanonicalLoopState = 
  | 'OPEN' 
  | 'PROPOSED' 
  | 'REVISION_REQUESTED' 
  | 'ACCEPTED' 
  | 'FINAL_REJECTED' 
  | 'DECLINED';

/**
 * Maps legacy states to v2.2 canonical states
 */
export function normalizeLoopState(state: CatchballLoopState): CanonicalLoopState {
  switch (state) {
    case 'drafting':
    case 'tossed_to_middle':
    case 'middle_working':
    case 'tossed_to_bottom':
    case 'bottom_working':
    case 'returned_to_middle':
    case 'OPEN':
      return 'OPEN';
    case 'returned_to_top':
    case 'executive_review':
    case 'proposed':
    case 'PROPOSED':
      return 'PROPOSED';
    case 'output_ready':
    case 'accepted_provisional':
    case 'accepted':
    case 'ACCEPTED':
      return 'ACCEPTED';
    case 'returned':
    case 'revision_requested':
    case 'REVISION_REQUESTED':
      return 'REVISION_REQUESTED';
    case 'rejected':
    case 'FINAL_REJECTED':
      return 'FINAL_REJECTED';
    case 'closed':
    case 'DECLINED':
      return 'DECLINED';
    default:
      return 'OPEN';
  }
}

/**
 * Ball possession levels - derived from loop state.
 *
 * DISPLAY-ONLY (ADR-catchball-consolidation). This three-value ternary is a
 * demoted display façade. Authority = per-branch position view
 * (`catchball_active_branch_positions`), surfaced through useOrgAwareCatchball
 * (holderOrgUnitId / holderDepth / holderTier / getActivePositions).
 * Do not read this enum as authority for who holds the ball or who may act —
 * bucket depth for friendly labels only. See ADR.
 */
export type BallPossession = 'executive' | 'middle' | 'bottom';

/**
 * Derives a user's tier (executive/middle/bottom) from their org depth relative to
 * the loop's root node and the subtree's max depth.
 * Used by useOrgAwareCatchball when loop.orgScopeNodeId is set.
 */
export function deriveTierFromOrgDepth(
  userDepth: number,
  rootDepth: number,
  maxDepth: number
): BallPossession {
  if (userDepth <= rootDepth) return 'executive';
  if (userDepth >= maxDepth) return 'bottom';
  return 'middle';
}

/**
 * Derives the Ball's current stance and direction from loop state.
 * Stance is always computed - never stored independently.
 */
export type BallStance =
  | 'request'
  | 'proposal'
  | 'transit-down'
  | 'transit-up'
  | 'complete';

/**
 * Mapping of loop states to ball possession.
 * DISPLAY-ONLY façade — Authority = per-branch position view. See ADR.
 */
export const LOOP_STATE_TO_POSSESSION: Record<CatchballLoopState, BallPossession> = {
  // v2.2 Canonical states
  'OPEN': 'middle',
  'PROPOSED': 'executive',
  'REVISION_REQUESTED': 'middle',
  'ACCEPTED': 'middle',
  'FINAL_REJECTED': 'executive',
  'DECLINED': 'executive',
  // Legacy states
  'drafting': 'executive',
  'tossed_to_middle': 'middle',
  'middle_working': 'middle',
  'tossed_to_bottom': 'bottom',
  'bottom_working': 'bottom',
  'returned_to_middle': 'middle',
  'returned_to_top': 'executive',
  'executive_review': 'executive',
  'output_ready': 'executive',
  'closed': 'executive',
  // Proposal packet states
  'proposed': 'executive',           // Exec has the ball to review
  'accepted_provisional': 'bottom',  // Ball goes back downstream to continue work
  'returned': 'bottom',              // Ball goes back downstream to address blockers
  'rejected': 'bottom',              // Ball goes back downstream
  'revision_requested': 'middle',    // Middle needs to revise
  'accepted': 'middle',              // Same as ACCEPTED, and as the database's _catchball_tier_for_state
};

/**
 * Get ball possession from loop state.
 *
 * DISPLAY-ONLY façade (ADR-catchball-consolidation). Authority = per-branch
 * position view via useOrgAwareCatchball / getActivePositions. Callers use this
 * only as a fallback label for loops that have no level turns yet. See ADR.
 */
export function getBallPossession(state: CatchballLoopState): BallPossession {
  return LOOP_STATE_TO_POSSESSION[state];
}

/**
 * Derives the Ball's current stance and direction from loop state.
 * Stance is always computed - never stored independently.
 */
export function getBallStance(state: CatchballLoopState): BallStance {
  switch (state) {
    case 'drafting':
    case 'middle_working':
    case 'bottom_working':
      return 'request';

    case 'tossed_to_middle':
    case 'tossed_to_bottom':
      return 'transit-down';

    case 'returned_to_middle':
    case 'returned_to_top':
      return 'transit-up';

    case 'executive_review':
    case 'proposed':
    case 'accepted_provisional':
    case 'output_ready':
    case 'PROPOSED':
    case 'REVISION_REQUESTED':
    case 'ACCEPTED':
      return 'proposal';

    case 'closed':
    case 'FINAL_REJECTED':
    case 'rejected':
      return 'complete';

    case 'OPEN':
    default:
      return 'request';
  }
}

// ============================================================================
// Loop Configuration
// ============================================================================

/**
 * Configuration for a level in the Catchball hierarchy
 */
export interface CatchballLevelParticipant {
  /** Empty string when this entry represents a specific group or an
   *  org-unit-wide fallback rather than one named user. */
  userId: string;
  role: 'lead' | 'participant' | 'observer';
  orgUnitId?: string;
  /** Explicit group assignment — recipients resolve to exactly this group's
   *  members, not every group placed at orgUnitId. */
  groupId?: string;
}

/**
 * Configuration for the Catchball hierarchy within a loop
 * Can be inherited from cycle or overridden per loop
 */
export interface CatchballLevelConfig {
  /** Executive level participants */
  executive: CatchballLevelParticipant[];
  
  /** Participants at the next org level down */
  middle: CatchballLevelParticipant[];
  
  /** Individual contributor participants (or representatives) */
  bottom: CatchballLevelParticipant[];
  
  /** Whether bottom level uses representatives instead of all ICs */
  bottomRepresentedByLeads: boolean;

  /** Response deadline in days (per level transition) */
  defaultResponseDeadlineDays: number;

  /**
   * Optional per-level cadence plan (T-minus machinery, 2026-07-18): one entry
   * per descent stop below the anchor, in descent order. windowDays is the
   * level's response window; dueDate is the calendar date the cycle designer
   * computed for it (null when the design had no dates). Additive — consumers
   * that only read defaultResponseDeadlineDays are unaffected.
   */
  levelDeadlines?: Array<{
    orgUnitId: string;
    windowDays: number;
    dueDate: string | null;
  }>;
}

// ============================================================================
// Intent and Outputs
// ============================================================================

/**
 * Executive intent passed down at the start of a loop
 */
export type LegitimacyMarking =
  | 'draft'
  | 'proposed'
  | 'preliminary';

/**
 * A clarification request - sent upward without transferring ball possession.
 * Does not advance loop state; records the question and any response.
 */
export interface LoopClarification {
  id: string;
  question: string;
  askedBy: string;
  askedAt: string;
  response?: string;
  respondedBy?: string;
  respondedAt?: string;
}

export interface LoopIntentOstContext {
  treeId: string;
  treeName: string;
  nodeIds: string[];
  outcomeNodeId?: string;
  outcomeTitle?: string;
  targetedOpportunityId?: string;
  targetedOpportunityTitle?: string;
  solutionNodeIds?: string[];
  solutionTitles?: string[];
  linkedAt: string;
}

export interface LoopIntent {
  /**
   * Which Path in the Cycle Designer this Loop runs over, and what the designer
   * called it.
   *
   * Canon 29 §2.3 says a Loop runs over exactly one Path, but until now the
   * runtime could not name it: `pathSlotId` never left the cycle-designer
   * feature, and `pathToLevelConfig` flattens a path into executive/middle/bottom
   * buckets, discarding which path it was. The route survived as org unit ids on
   * the Path Instance; its identity did not. Surfaces could draw the walk but not
   * title it.
   *
   * Optional because loops created before this shipped, and loops created outside
   * the Designer, legitimately have neither.
   */
  pathSlotId?: string;
  pathName?: string;

  /** Strategic context (vision, strategy, no-fail goals) */
  strategicContext: string;
  
  /** Specific asks or questions for this loop */
  asks: string[];
  
  /** Constraints (budget, headcount, time) */
  constraints: string[];

  /**
   * Marks how much latitude the receiving tier has to challenge this request.
   * Travels downward with the Request Ball.
   * Defaults to 'proposed' if absent.
   */
  legitimacyMarking?: LegitimacyMarking;

  /**
   * Soft directional guidance - distinct from hard constraints.
   * Constraints block; guardrails guide.
   */
  guardrails?: string[];

  /**
   * What a good proposal looks like - what questions it should answer,
   * what level of detail is expected, what would cause a return.
   */
  responseGuidance?: string;

  /**
   * The receiving tier's written interpretation of this request.
   * Captures ambiguities, tensions, and translation decisions.
   */
  interpretation?: string;

  /**
   * When a proposal is due from the receiving tier.
   * ISO 8601 date string.
   */
  dueDate?: string;

  /**
   * Maximum number of loop iterations before escalation.
   * null = no limit.
   */
  maxIterations?: number | null;

  /**
   * Clarification requests sent upward without returning the ball.
   * Each entry is a question or challenge from the receiving tier.
   */
  clarifications?: LoopClarification[];
  
  /** What kind of response is expected */
  expectedResponseType: 'options' | 'feasibility' | 'trade-offs' | 'sequencing';
  
  /** Attachments (document IDs, links) */
  attachmentIds?: string[];

  /** Org nodes receiving the toss (when cycle has orgScopeNodeId) */
  recipientOrgUnitIds?: string[];

  /** Opportunity solution tree context carried inside the ball */
  ostContext?: LoopIntentOstContext;

  /** Item types that are in scope for this request (e.g. opportunity, investment) */
  inScopeItemTypes?: string[];

  /** Expected number of proposals from the receiving tier (e.g. "5–10") */
  proposalCountGuidance?: string;

  /** Types of evidence the receiving tier should gather and cite */
  evidenceExpectations?: string[];

  /**
   * Whether the initiating level imposes criteria + weights ('imposed')
   * or the receiving tier defines their own comparison framework ('team-defined').
   */
  criteriaGovernance?: 'imposed' | 'team-defined';

  /** Criteria definitions when criteriaGovernance is 'imposed' */
  imposedCriteria?: Array<{ name: string; weight: number; description?: string }>;
}

const LOOP_INTENT_RESPONSE_TYPES = new Set<LoopIntent['expectedResponseType']>([
  'options',
  'feasibility',
  'trade-offs',
  'sequencing',
]);

/** Coerce persisted intent list fields (arrays, legacy strings, semicolon lists) to string[]. */
export function normalizeStringList(value: unknown, splitDelimiter?: string): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (splitDelimiter && trimmed.includes(splitDelimiter)) {
      return trimmed.split(splitDelimiter).map((part) => part.trim()).filter(Boolean);
    }
    return [trimmed];
  }
  return [];
}

/** Normalize loop intent JSON from Supabase (handles legacy `{ text }` payloads and string constraints). */
export function normalizeLoopIntent(raw: unknown): LoopIntent | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }

  const record = raw as Record<string, unknown>;
  const legacyText = typeof record.text === 'string' ? record.text : '';
  const strategicContext =
    typeof record.strategicContext === 'string' ? record.strategicContext : legacyText;

  const guardrails = normalizeStringList(record.guardrails, ';');
  const evidenceExpectations = normalizeStringList(record.evidenceExpectations);
  const inScopeItemTypes = normalizeStringList(record.inScopeItemTypes);
  const recipientOrgUnitIds = normalizeStringList(record.recipientOrgUnitIds);
  const attachmentIds = normalizeStringList(record.attachmentIds);

  const expectedResponseType = LOOP_INTENT_RESPONSE_TYPES.has(
    record.expectedResponseType as LoopIntent['expectedResponseType'],
  )
    ? (record.expectedResponseType as LoopIntent['expectedResponseType'])
    : 'options';

  const intent: LoopIntent = {
    ...(record as Partial<LoopIntent>),
    strategicContext,
    asks: normalizeStringList(record.asks),
    constraints: normalizeStringList(record.constraints, ';'),
    expectedResponseType,
  };

  if (guardrails.length > 0) intent.guardrails = guardrails;
  else delete intent.guardrails;

  if (evidenceExpectations.length > 0) intent.evidenceExpectations = evidenceExpectations;
  else delete intent.evidenceExpectations;

  if (inScopeItemTypes.length > 0) intent.inScopeItemTypes = inScopeItemTypes;
  else delete intent.inScopeItemTypes;

  if (recipientOrgUnitIds.length > 0) intent.recipientOrgUnitIds = recipientOrgUnitIds;
  else delete intent.recipientOrgUnitIds;

  if (attachmentIds.length > 0) intent.attachmentIds = attachmentIds;
  else delete intent.attachmentIds;

  return intent;
}

/**
 * Synthesis returned from middle to executive
 */
export interface LoopSynthesis {
  /** Summary of findings */
  summary: string;
  
  /** Key tensions or trade-offs identified */
  tensions: string[];
  
  /** Recommendations */
  recommendations: string[];
  
  /** Item Pools produced (maps to item_pool_ids in DB) */
  focusListIds: string[];
  
  /** Unresolved items requiring executive decision */
  unresolvedItems: string[];
  
  /** Created by */
  synthesizedBy: string;
  synthesizedAt: Date;
}

// ============================================================================
// Main Interface
// ============================================================================

/**
 * A Catchball Loop - a convergence mechanism inside a cycle
 *
 * Per canon (`01-terminology-and-glossary_v3.md`):
 * - Loop begins with a downward Request and may collect **one or more** upward Proposal Submission
 *   handoffs when **multiple Balls** return (e.g. division branches).
 * - Reconciliation merges or selects among those Proposals per workflow.
 * - A Revision Requested event creates a new Loop (same Track, same Cycle)
 */
export interface CatchballLoop {
  id: CatchballLoopId;
  cycleId: string;
  
  /**
   * The Track this loop belongs to (required per v2.3 architecture).
   * Loops cannot cross Track boundaries.
   */
  trackId: string;

  /** Loop number within the cycle (1, 2, 3...) */
  loopNumber: number;
  
  /** Current state */
  state: CatchballLoopState;
  
  /** Configuration for this loop's hierarchy */
  levelConfig: CatchballLevelConfig;
  
  /** Executive intent (set when tossing to middle) */
  intent?: LoopIntent;
  
  /** Middle layer synthesis (set when returning to top) */
  synthesis?: LoopSynthesis;
  
  /** Item Pools produced by this loop (maps to item_pool_ids in DB) */
  focusListIds: string[];
  
  /** Session IDs created within this loop */
  sessionIds: string[];

  /**
   * Subset of `sessionIds` the holder has marked for the next pass-up
   * (`included_session_ids`, migration 670). Always a subset of `sessionIds`;
   * a session drops out of it once it backs a submitted proposal, so the desk
   * can tell a used sitting from one still waiting to go up. Written only by
   * `catchball_set_session_link` / `catchball_record_proposal_session`.
   */
  includedSessionIds: string[];

  /**
   * The ItemPool this loop operates on (Track link).
   * When set, the pool's category+altitude define the Track Cell.
   * Per Canon: Track = ItemPool with (requiredItemCategory × requiredAltitudeLevel).
   */
  itemPoolId?: string;

  /** Whether executive requested another round */
  anotherRoundRequested: boolean;
  
  // =========================================================================
  // v2.2 Canon: Request/Proposal Lifecycle Fields
  // =========================================================================
  
  /**
   * When the initial Request was issued (Ball tossed down).
   * Per Canon: "Loop instance must track request_issued_at/by/payload_ref"
   */
  requestIssuedAt?: Date;
  
  /**
   * Who issued the initial Request.
   */
  requestIssuedBy?: string;
  
  /**
   * Reference to the Request payload (constraints, objectives, asks).
   * Stored separately, this is a reference.
   */
  requestPayloadRef?: {
    entityId: string;
    entityTable: string;
  };
  
  /**
   * When an upward Proposal was submitted (Ball tossed up); see also `catchball_proposals` by loop.
   */
  proposalSubmittedAt?: Date;
  
  /**
   * Optional primary Proposal artifact id for this loop when the runtime stores a single pointer.
   * Multiple upward packets may exist for the same loop — use `catchball_proposals.loop_id`.
   */
  proposalId?: string;
  
  /**
   * Final S6 Session Result ID for this loop.
   * Per Canon: "Each Track culminates in exactly one final S6 Session Result per Loop"
   */
  finalS6SessionResultId?: string;
  
  /**
   * Iteration number (computed from PROPOSAL_SUBMITTED events, but may be cached)
   * Per Canon: "Loop Iteration: Computed (not stored) count of PROPOSAL_SUBMITTED events"
   */
  iterationNumber?: number;
  
  // =========================================================================
  // Timestamps
  // =========================================================================
  
  /** Timestamps */
  createdAt: Date;
  updatedAt: Date;
  closedAt?: Date;
  
  /** Audit trail */
  createdBy: string;
  
  /** Ball holder tracking (who currently "has the ball" in the loop) */
  ballHolderScope?: 'executive' | 'middle' | 'bottom' | 'system';
  ballHolderUserId?: string;
  /** When the current holder claimed (or was auto-assigned) the ball. ISO timestamp. */
  ballClaimedAt?: string;

  /**
   * The org unit node that anchors this loop.
   * Relative tier depth is computed from this node.
   * Users at this node depth = executive tier for this loop.
   * Users one level below = first mid-management tier.
   * Users at max depth below = IC tier.
   */
  orgScopeNodeId?: string;
}

// ============================================================================
// State Transitions
// ============================================================================

/**
 * Allowed state transitions for Catchball Loops
 */
export const LOOP_TRANSITIONS: Record<CatchballLoopState, CatchballLoopState[]> = {
  // v2.2 Canonical transitions
  'OPEN': ['PROPOSED', 'DECLINED'],
  'PROPOSED': ['ACCEPTED', 'REVISION_REQUESTED', 'FINAL_REJECTED', 'DECLINED'],
  'REVISION_REQUESTED': ['OPEN', 'PROPOSED', 'DECLINED'],
  'ACCEPTED': ['DECLINED'],
  'FINAL_REJECTED': [],
  'DECLINED': [],
  // Legacy transitions
  'drafting': ['tossed_to_middle', 'closed', 'OPEN'],
  'tossed_to_middle': ['middle_working'],
  'middle_working': ['tossed_to_bottom', 'returned_to_top', 'proposed'],
  'tossed_to_bottom': ['bottom_working'],
  'bottom_working': ['returned_to_middle'],
  'returned_to_middle': ['returned_to_top', 'tossed_to_bottom', 'proposed'],
  'returned_to_top': ['executive_review', 'PROPOSED'],
  'executive_review': ['output_ready', 'closed', 'accepted_provisional', 'returned', 'rejected'],
  'output_ready': ['closed', 'ACCEPTED'],
  'closed': ['DECLINED'],
  // Proposal packet transitions
  'proposed': ['accepted_provisional', 'returned', 'rejected', 'PROPOSED'],
  'accepted_provisional': ['closed', 'ACCEPTED'],
  'returned': ['proposed', 'closed', 'REVISION_REQUESTED'],
  'rejected': ['closed', 'FINAL_REJECTED'],
  'revision_requested': ['OPEN', 'proposed'],
  'accepted': ['closed', 'DECLINED'],
};

/**
 * Check if a state transition is allowed
 */
export function canTransitionLoop(
  from: CatchballLoopState, 
  to: CatchballLoopState
): boolean {
  return LOOP_TRANSITIONS[from].includes(to);
}

/**
 * Transition event for audit
 */
export interface LoopTransitionEvent {
  loopId: CatchballLoopId;
  from: CatchballLoopState;
  to: CatchballLoopState;
  transitionedBy: string;
  transitionedAt: Date;
  note?: string;
}

// ============================================================================
// Loop Order
// ============================================================================

/**
 * Canonical order of loop states for progress display (v2.2)
 */
export const LOOP_STATE_ORDER_V22: CanonicalLoopState[] = [
  'OPEN',
  'PROPOSED',
  'REVISION_REQUESTED',
  'ACCEPTED',
  'FINAL_REJECTED',
  'DECLINED',
];

/**
 * Legacy order of loop states for progress display
 */
export const LOOP_STATE_ORDER: CatchballLoopState[] = [
  'drafting',
  'tossed_to_middle',
  'middle_working',
  'tossed_to_bottom',
  'bottom_working',
  'returned_to_middle',
  'returned_to_top',
  'executive_review',
  'output_ready',
  'closed',
];

/**
 * Get progress percentage for a loop state
 */
export function getLoopProgress(state: CatchballLoopState): number {
  const index = LOOP_STATE_ORDER.indexOf(state);
  if (index === -1) return 0;
  // output_ready is 100%, closed is also 100%
  if (state === 'output_ready' || state === 'closed') return 100;
  return Math.round((index / (LOOP_STATE_ORDER.length - 2)) * 100);
}

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Human-readable labels for loop states
 */
export const LOOP_STATE_LABELS: Record<CatchballLoopState, string> = {
  // v2.2 Canonical states
  'OPEN': 'In Progress',
  'PROPOSED': 'Awaiting Response',
  'REVISION_REQUESTED': 'Revision Requested',
  'ACCEPTED': 'Accepted',
  'FINAL_REJECTED': 'Finally Rejected',
  'DECLINED': 'Declined',
  // Legacy states
  'drafting': 'Setting Up',
  'tossed_to_middle': 'Sent to Management',
  'middle_working': 'Management Working',
  'tossed_to_bottom': 'Sent to Teams',
  'bottom_working': 'Teams Working',
  'returned_to_middle': 'Teams Returned',
  'returned_to_top': 'Synthesis Submitted',
  'executive_review': 'Executive Review',
  'output_ready': 'Proposed Set Ready',
  'closed': 'Closed',
  // Proposal packet states
  'proposed': 'Proposal Submitted',
  'accepted_provisional': 'Accepted Provisionally',
  'returned': 'Returned for Revision',
  'rejected': 'Rejected',
  'revision_requested': 'Revision Requested',
  'accepted': 'Accepted',
};

/**
 * Ball possession labels
 */
export const POSSESSION_LABELS: Record<BallPossession, string> = {
  'executive': 'Executive Leadership',
  'middle': 'Middle Management',
  'bottom': 'Individual Contributors',
};
